/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { ID } from '@gh/common-shared-data';
import {
  AbstractRestService,
  arrayOf,
  emptyOf,
  optimisticLockingOf,
  RestSettings,
  SlicedDataSource,
} from '@gh/core-data';
import { Observable } from 'rxjs';
import { CheckUsageDto } from '../common/check-usage.dto';
import { CheckUsageMapper } from '../common/check-usage.mapper';
import { TakeOffParamDto, TakeOffParamSharedListItemDto } from './top.dto';
import { TakeOffParamMapper, TakeOffParamSharedListItemMapper } from './top.mapper';

export type TakeOffParamsGridFilter = {
  showDeleted: boolean;
};

@Injectable()
export class TakeOffParamsRestService extends AbstractRestService {

  /**
   * @returns {{takeOffParamId: number, code: string, gtoUom: any, gtoStyle: any, gtoColor: any, description: string, version: number, isActive: boolean}}
   */
  static prepareListForCreate(): TakeOffParamDto {
    return {
      takeOffParamId: '',
      code: '',
      gtoUom: void 0,
      isSystem: false,
      gtoStyle: void 0,
      gtoColor: void 0,
      description: '',
      version: 0,
      isActive: false,
    };
  }

  constructor(settings: RestSettings) {
    super(settings);
  }

  /**
   * Grid data source for list of Take Off Params
   * @param filter
   * @returns {SlicedDataSource<TakeOffParamDto>}
   */
  findAllAsDataSource(filter: TakeOffParamsGridFilter): SlicedDataSource<TakeOffParamDto> {
    return this.createSlicedDataSourceGet(
      `${this.bp}/take-off-params`, filter, void 0, arrayOf(TakeOffParamMapper));
  }

  findAllShared(): Observable<TakeOffParamSharedListItemDto[]> {
    return this.httpGet(`${this.bp}/take-off-params/shared`, arrayOf(TakeOffParamSharedListItemMapper));
  }

  /**
   * Checks if Take Off Param in usage or not
   * @param takeOffParam
   * @returns {Observable<CheckUsageDto>}
   */
  checkUsage(takeOffParam: TakeOffParamDto): Observable<CheckUsageDto> {
    const { takeOffParamId: id, version } = takeOffParam;
    return this.httpGet(
      `${this.bp}/take-off-params/check-usage/${id}`,
      { version },
      void 0,
      CheckUsageMapper,
    );
  }

  /**
   * Undelete (activate sense) ToP
   * @param takeOffParam
   * @returns {Observable<TakeOffParamDto>}
   */
  activate(takeOffParam: TakeOffParamDto): Observable<TakeOffParamDto> {
    return this.httpPut(
      `${this.bp}/take-off-params/undelete/${takeOffParam.takeOffParamId}`,
      takeOffParam,
      emptyOf(optimisticLockingOf(TakeOffParamMapper)));
  }

  /**
   * Remove (deactivate sense) ToP
   * @param {TakeOffParamDto} takeOffParam
   * @param {CheckUsageDto} takeOffParamCheckUsage
   * @returns {Observable<TakeOffParamDto>}
   */
  deactivate(takeOffParam: TakeOffParamDto,
             takeOffParamCheckUsage: CheckUsageDto): Observable<CheckUsageDto> {
    return this.httpDelete(
      `${this.bp}/take-off-params/${takeOffParam.takeOffParamId}`,
      takeOffParamCheckUsage,
      CheckUsageMapper);
  }

  /**
   * Single Take Off Param Observable
   * @param id
   * @returns {Observable<TakeOffParamDto>}
   */
  get(id: ID): Observable<TakeOffParamDto> {
    return this.httpGet(`${this.bp}/take-off-params/${id}`, optimisticLockingOf(TakeOffParamMapper));
  }

  /**
   * Single Take Off Param Observable
   * @param id
   * @returns {Observable<TakeOffParamDto>}
   */
  getShared(id: ID): Observable<TakeOffParamDto> {
    return this.httpGet(`${this.bp}/take-off-params/${id}/shared`, optimisticLockingOf(TakeOffParamMapper));
  }

  /**
   * Create new Take Off Param Observable
   * @param takeOffParam
   * @returns {Observable<TakeOffParamDto>}
   */
  create(takeOffParam: TakeOffParamDto): Observable<TakeOffParamDto> {
    return this.httpPost(`${this.bp}/take-off-params`, takeOffParam, TakeOffParamMapper);
  }

  /**
   * Update single Take Off Param Observable
   * @param takeOffParam
   * @returns {Observable<TakeOffParamDto>}
   */
  update(takeOffParam: TakeOffParamDto): Observable<TakeOffParamDto> {
    return this.httpPut(
      `${this.bp}/take-off-params/${takeOffParam.takeOffParamId}`, takeOffParam, optimisticLockingOf(TakeOffParamMapper));
  }
}
